/**
 * Meeting Log persistence (meeting_archives).
 */

import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';
import { downloadMinutesExport } from './exportMinutesFile';
import {
  getMinuteEntryDisplayText,
  minuteExportSuppressesAdoptedDefeatedSuffix,
} from './motionMinuteFormatting';
import { MOTION_TYPES } from './motionTypes';
import { getQuorumStatus } from './quorumUtils';
import { formatTime } from './timeUtils';

function defaultTitle(setup, startedAt) {
  const org = String(setup?.organizationName || '').trim();
  const d = startedAt ? new Date(startedAt) : new Date();
  const dateLabel = Number.isNaN(d.getTime())
    ? new Date().toLocaleDateString()
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  return org ? `${org} — ${dateLabel}` : `Meeting — ${dateLabel}`;
}

/**
 * Persist a minutes snapshot to the Meeting Log.
 * @returns {Promise<object|null>} inserted row or null if skipped / unavailable
 */
export async function saveMeetingArchive({
  organizationId,
  meetingSessionId = null,
  meetingMinutes = [],
  meetingSetup = {},
  meetingStartTime = null,
  meetingEndTime = null,
  meetingTimer = 0,
  title = null,
}) {
  if (!hasSupabaseConfig() || !organizationId) return null;
  const entries = Array.isArray(meetingMinutes) ? meetingMinutes : [];
  if (!entries.length) return null;

  const supabase = getSupabaseClient();
  if (!supabase) return null;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  const startedAt =
    meetingStartTime ||
    meetingSetup?.meetingStartTime ||
    null;
  const endedAt = meetingEndTime || new Date().toISOString();

  const payload = {
    organization_id: organizationId,
    meeting_session_id: meetingSessionId || null,
    title: title || defaultTitle(meetingSetup, startedAt || endedAt),
    started_at: startedAt ? new Date(startedAt).toISOString() : null,
    ended_at: new Date(endedAt).toISOString(),
    present_members:
      typeof meetingSetup.presentMembers === 'number' ? meetingSetup.presentMembers : null,
    total_members: typeof meetingSetup.totalMembers === 'number' ? meetingSetup.totalMembers : null,
    minutes: entries,
    meeting_setup_snapshot: {
      organizationName: meetingSetup.organizationName || '',
      quorumType: meetingSetup.quorumType,
      quorumRequirement: meetingSetup.quorumRequirement,
      presentMembers: meetingSetup.presentMembers,
      totalMembers: meetingSetup.totalMembers,
      orderOfBusiness: meetingSetup.orderOfBusiness || [],
      meetingTimer: meetingTimer || 0,
      meetingStartTime: startedAt,
      meetingEndTime: endedAt,
    },
    created_by: user?.id || null,
  };

  const { data, error } = await supabase.from('meeting_archives').insert(payload).select('*').single();
  if (error) throw error;
  return data;
}

export async function listMeetingArchives(organizationId, { limit = 100 } = {}) {
  if (!hasSupabaseConfig() || !organizationId) return [];
  const supabase = getSupabaseClient();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('meeting_archives')
    .select(
      'id, organization_id, title, started_at, ended_at, present_members, total_members, created_at'
    )
    .eq('organization_id', organizationId)
    .order('ended_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function fetchMeetingArchive(archiveId) {
  if (!hasSupabaseConfig() || !archiveId) return null;
  const supabase = getSupabaseClient();
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('meeting_archives')
    .select('*')
    .eq('id', archiveId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Admin-only hard delete of a Meeting Log archive. */
export async function deleteMeetingArchive(archiveId) {
  if (!hasSupabaseConfig() || !archiveId) throw new Error('Not configured');
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Not configured');
  const { error } = await supabase.from('meeting_archives').delete().eq('id', archiveId);
  if (error) throw error;
  return true;
}

/**
 * Build plain-text minutes for archive re-export (aligned with live hamburger export).
 */
export function formatArchiveMinutesText(archive) {
  const setup = archive?.meeting_setup_snapshot || {};
  const orgName = setup.organizationName || '';
  const startIso = archive?.started_at || setup.meetingStartTime || null;
  const endIso = archive?.ended_at || setup.meetingEndTime || null;
  const meetingSetupForQuorum = {
    organizationName: orgName,
    presentMembers: archive?.present_members ?? setup.presentMembers,
    totalMembers: archive?.total_members ?? setup.totalMembers,
    quorumType: setup.quorumType,
    quorumRequirement: setup.quorumRequirement,
  };

  const entries = Array.isArray(archive?.minutes) ? [...archive.minutes] : [];
  entries.sort((a, b) => (a.chronologicalOrder || 0) - (b.chronologicalOrder || 0));

  let out = '';
  out += `===============================================================\n`;
  out += `                        MINUTES OF MEETING\n`;
  if (orgName) out += `                    ${String(orgName).toUpperCase()}\n`;
  out += `===============================================================\n\n`;
  out += `MEETING INFORMATION:\n`;
  out += `-------------------------------------------------------------\n`;
  if (orgName) out += `Organization:        ${orgName}\n`;

  if (startIso) {
    const start = new Date(startIso);
    if (!Number.isNaN(start.getTime())) {
      out += `Date:                ${start.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}\n`;
      out += `Called to Order:     ${start.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })}\n`;
    }
  }

  if (endIso) {
    const end = new Date(endIso);
    if (!Number.isNaN(end.getTime())) {
      out += `Adjourned:           ${end.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })}\n`;
    }
  }

  const timer = Number(setup.meetingTimer) || 0;
  if (timer > 0) {
    out += `Duration:            ${formatTime(timer)}\n`;
  }

  if (meetingSetupForQuorum.totalMembers && meetingSetupForQuorum.presentMembers) {
    const quorumStatus = getQuorumStatus(meetingSetupForQuorum);
    out += `Quorum Status:       ${quorumStatus.text} (${quorumStatus.details})\n`;
  } else if (archive?.present_members != null && archive?.total_members != null) {
    out += `Attendance:          ${archive.present_members} / ${archive.total_members}\n`;
  }

  out += `\n`;

  const official = entries.filter(
    (e) => !e.activityOnly && e.type !== 'objection' && e.type !== 'uc_request'
  );

  if (official.length > 0) {
    out += `PROCEEDINGS:\n`;
    out += `-------------------------------------------------------------\n\n`;
    official.forEach((entry) => {
      const textToUse = getMinuteEntryDisplayText(entry, MOTION_TYPES);
      let entryText = `${entry.timestamp || ''} - ${textToUse}`;
      if (
        entry.status === 'completed' &&
        entry.motionData &&
        !minuteExportSuppressesAdoptedDefeatedSuffix(entry, MOTION_TYPES)
      ) {
        const status = entry.motionData.result || entry.motionData.status;
        if (status === 'voted_passed' || status === 'passed') {
          entryText += ' - ADOPTED';
        } else if (status === 'voted_failed' || status === 'failed') {
          entryText += ' - DEFEATED';
        } else if (status === 'passed_by_uc') {
          entryText += ' - ADOPTED BY UNANIMOUS CONSENT';
        } else if (status === 'withdrawn') {
          entryText += ' - WITHDRAWN';
        } else if (status === 'tabled') {
          entryText += ' - LAID ON THE TABLE';
        } else if (status === 'postponed') {
          entryText += ' - POSTPONED';
        }
      } else if (entry.result) {
        entryText += ` - ${entry.result}`;
      }
      out += `${entryText}\n`;
    });
    out += `\n`;
  } else {
    out += `No formal business was conducted during this meeting.\n\n`;
  }

  out += `\n\n`;
  out += `===============================================================\n`;
  out += `                        ATTESTATION\n`;
  out += `===============================================================\n\n`;
  out += `These minutes were compiled from proceedings recorded during the\n`;
  out += `above-referenced meeting and accurately reflect the business\n`;
  out += `conducted and decisions made.\n\n`;
  out += `Minutes prepared: ${new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })} at ${new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })}\n\n`;
  out += `Secretary: ______________________________ Date: __________\n\n`;
  out += `Chair: __________________________________ Date: __________\n\n`;
  out += `-------------------------------------------------------------\n`;
  out += `Generated by Pocket Parliamentarian\n`;
  out += `Parliamentary Meeting Management System\n`;
  out += `-------------------------------------------------------------\n`;
  return out;
}

/**
 * Re-export an archived meeting as PDF, Word, or TXT (same as hamburger menu).
 * @param {object} archive
 * @param {'pdf'|'doc'|'txt'} format
 */
export function exportArchiveMinutes(archive, format = 'pdf') {
  if (!archive) return;
  const content = formatArchiveMinutesText(archive);
  const setup = archive.meeting_setup_snapshot || {};
  const org = setup.organizationName || 'Organization';
  const dateLabel = (archive.ended_at || archive.started_at
    ? new Date(archive.ended_at || archive.started_at)
    : new Date()
  ).toLocaleDateString().replace(/\//g, '-');
  const fileName = `Meeting_Minutes_${org}_${dateLabel}`;
  downloadMinutesExport(content, fileName, format);
}

export function downloadTextFile(filename, content) {
  downloadMinutesExport(content, filename.replace(/\.txt$/i, ''), 'txt');
}
